/**
 * History recompute engine (R6-1) — the 3D HISTORY command.
 *
 * When Record History is ON, geometry-producing commands register the
 * output entity ids against their source entities plus a pure recompute
 * closure. When a source changes (through the store's entity-update path),
 * the dependents are rebuilt and re-emitted — the same kernel math the
 * original command used, now driven by the new input. Circular guards +
 * a depth limit keep recursion bounded.
 *
 * Provenance lives inside the entity data bag (`data.history`) so it
 * survives serialization and undo, and the ledger mirror carries it for
 * free. `sys.historyToggle` flips `useViewStore.recordHistory`, which this
 * module consults — the flag finally has a consumer.
 */

import { create } from 'zustand';
import { useAppStore } from '../../state/store';
import type { Entity } from '../../state/store';
import { useViewStore } from '../../state/commandAuxStores';
import { notify } from '../../utils/notify';
import i18n from '../../i18n';

export interface HistoryEntry {
    /** Output entity ids this record owns. */
    outputs: string[];
    /** Source entity ids the outputs were built from. */
    inputs: string[];
    /** Command that produced them (for reporting). */
    actionId: string;
    /**
     * Pure rebuild: given the current source entities, return replacement
     * entities for `outputs` (fresh data, same ids keep selection alive).
     * Returning null means "cannot recompute" (source deleted/unsupported).
     */
    recompute: (sources: Entity[]) => Entity[] | null;
}

interface HistoryState {
    records: HistoryEntry[];
    register: (entry: HistoryEntry) => void;
    removeFor: (outputIds: string[]) => void;
    /** True while a recompute cascade is running (suppress re-registration). */
    recomputing: boolean;
}

export const useHistoryStore = create<HistoryState>((set) => ({
    records: [],
    register: (entry) => set((s) => ({
        records: [...s.records.filter((r) => !r.outputs.some((o) => entry.outputs.includes(o))), entry],
    })),
    removeFor: (outputIds) => set((s) => ({
        records: s.records.filter((r) => !r.outputs.some((o) => outputIds.includes(o))),
    })),
    recomputing: false,
}));

/** Whether new command outputs should record history (3D HISTORY flag). */
export function historyRecording(): boolean {
    return useViewStore.getState().recordHistory && !useHistoryStore.getState().recomputing;
}

/**
 * Register provenance for freshly created entities. Call from runners right
 * after undoableAdd when their geometry derived from picked sources.
 */
export function recordHistory(
    actionId: string,
    inputs: string[],
    outputs: string[],
    recompute: (sources: Entity[]) => Entity[] | null
): void {
    if (!historyRecording() || inputs.length === 0 || outputs.length === 0) return;
    useHistoryStore.getState().register({ outputs, inputs, actionId, recompute });
}

let started = false;

/**
 * Boot the recompute watcher (idempotent; called from CommandRegistrar).
 * Subscribes to entity data changes and replays dependent records.
 */
export function startHistoryEngine(): void {
    if (started) return;
    started = true;
    let depth = 0;
    useAppStore.subscribe((state, prev) => {
        if (state.entities === prev.entities || useHistoryStore.getState().recomputing) return;
        const changed = new Set<string>();
        Object.keys(state.entities).forEach((id) => {
            if (state.entities[id]?.data !== prev.entities[id]?.data) changed.add(id);
        });
        Object.keys(prev.entities).forEach((id) => {
            if (!state.entities[id]) changed.add(id); // deletion invalidates too
        });
        if (changed.size === 0) return;
        const { records } = useHistoryStore.getState();
        const affected = records.filter((r) => r.inputs.some((i) => changed.has(i)));
        if (affected.length === 0) return;
        if (depth >= 4) {
            notify(i18n.t('tools.notify.historyRecomputeDepthLimitReachedCircular'), 'warning');
            return;
        }
        depth++;
        useHistoryStore.setState({ recomputing: true });
        let rebuilt = 0;
        try {
            affected.forEach((rec) => {
                const sources = rec.inputs.map((i) => useAppStore.getState().entities[i]).filter(Boolean) as Entity[];
                const fresh = sources.length === rec.inputs.length ? rec.recompute(sources) : null;
                if (!fresh) {
                    useHistoryStore.getState().removeFor(rec.outputs);
                    return;
                }
                const app = useAppStore.getState();
                fresh.forEach((e) => {
                    // keep the registered output id stable so selection and
                    // later recomputes still address the same object.
                    app.updateEntity(e.id, { data: e.data });
                    rebuilt++;
                });
                useHistoryStore.getState().register({ ...rec, outputs: fresh.map((e) => e.id) });
            });
        } finally {
            useHistoryStore.setState({ recomputing: false });
            depth--;
        }
        if (rebuilt > 0) notify(i18n.t('tools.notify.historyRebuilt', { count: rebuilt }), 'info');
    });
}
